import { get, set, del, keys, delMany } from 'idb-keyval';
import { experimental_createQueryPersister } from '@tanstack/query-persist-client-core';
import type { Query, QueryFunctionContext, QueryKey } from '@tanstack/react-query';

// Persister POR QUERY (IndexedDB). Desacopla memoria de offline: cada query e
// persistida separada e restaurada sob demanda — pode sair da RAM (gcTime curto)
// sem perder o offline. Substitui o persistQueryClient (cache inteiro) que forcava
// gcTime alto (24h) = estouro de memoria. Padrao oficial TanStack p/ memoria + offline.
// 05/06: buster anti-acúmulo — bump invalida o cache offline antigo (removido no
// acesso), evita IndexedDB inchar ao longo de dias/deploys (uma das causas de OOM).
// 01/07: buster PARTICIONADO POR USUÁRIO — cache com keys globais (['leads'],
// ['pedidos']) num aparelho compartilhado restaurava dados RLS-filtrados do
// consultor A pro consultor B (indefinidamente se offline). O user.id de quem
// gravou assina o buster; user diferente → entrada descartada no acesso.
export const PERSIST_BUSTER_BASE = 'rq-2026-10-07b';

// O persister é criado no main.tsx ANTES do auth resolver — o user.id inicial é
// resolvido de forma lazy do snapshot em localStorage ('crm-user', mesmo que o
// useAppStore restaura). Sem snapshot (deslogado) assina como 'anon'.
function savedUserId(): string {
  try {
    const raw = localStorage.getItem('crm-user');
    if (raw) {
      const u = JSON.parse(raw) as { id?: string | number };
      if (u?.id != null) return String(u.id);
    }
  } catch { /* ignore */ }
  return 'anon';
}

function makeBuster(userId: string): string {
  return `${PERSIST_BUSTER_BASE}:u:${userId}`;
}

let currentUserId = savedUserId();

// Buster efetivo desta carga (base + usuário). main.tsx compara com o
// localStorage 'crm-rq-buster' pra podar o cache antigo proativamente — troca
// de usuário no aparelho poda o cache do anterior no boot seguinte.
export const PERSIST_BUSTER = makeBuster(currentUserId);

function makePersister(userId: string) {
  return experimental_createQueryPersister<string>({
    storage: {
      getItem: (key) => get<string>(key).then((v) => v ?? null),
      setItem: (key, value) => set(key, value),
      removeItem: (key) => del(key),
    },
    maxAge: 1000 * 60 * 60 * 24, // 24h — janela offline
    prefix: 'crm-rq',
    buster: makeBuster(userId),
    // Respeita queries que optaram por nao persistir (meta.persist:false)
    filters: { predicate: (q) => (q.meta as { persist?: boolean } | undefined)?.persist !== false },
  });
}

let activePersister = makePersister(currentUserId);

// Chamado quando o dono da sessão muda (login/SIGNED_IN/logout): recria o
// persister com o buster do novo usuário. O que o usuário anterior assinou
// deixa de validar e é removido no próximo acesso.
export function setPersisterUser(userId: string | null): void {
  const next = userId ?? 'anon';
  if (next === currentUserId) return;
  currentUserId = next;
  activePersister = makePersister(next);
  // Mantém o marcador do main.tsx coerente pra não podar o cache recém-assinado
  // no próximo boot (a poda só deve disparar quando o buster realmente mudou).
  try { localStorage.setItem('crm-rq-buster', makeBuster(next)); } catch { /* quota */ }
}

// Wrapper estável: main.tsx captura persisterFn UMA vez na criação do
// QueryClient; delega sempre pro persister ativo (recriado no setPersisterUser).
export const queryPersister = {
  persisterFn: <T, TQueryKey extends QueryKey>(
    queryFn: (context: QueryFunctionContext<TQueryKey>) => T | Promise<T>,
    ctx: QueryFunctionContext<TQueryKey>,
    query: Query,
  ): Promise<T> => activePersister.persisterFn(queryFn, ctx, query),
};

// Limpa TODO o cache offline persistido (idb crm-rq*). Chamado no logout:
// aparelho compartilhado não pode reidratar respostas RLS-filtradas de quem saiu.
// Só toca chaves do persister — preserva fila offline (crm-offline-mutations),
// check-ins (checkin_offline_queue) e municípios (ibge-municipios-v1).
export async function clearPersistedQueryCache(): Promise<void> {
  try {
    const ks = await keys();
    const stale = ks.filter((k): k is string => typeof k === 'string' && k.startsWith('crm-rq'));
    if (stale.length) await delMany(stale);
  } catch { /* IndexedDB indisponível — segue normalmente */ }
}
